import { v7 as uuidv7 } from 'uuid';

export type AnalyticsClientInitOptions = {
  endpoint: string;
  companyId: string;
  assetId: string;
};

export enum AnalyticsEventType {
  PageView = 'page_view',
}

type PageViewEvent = {
  event_type: AnalyticsEventType.PageView;
  client_ts: number;
  url: string;
  referrer: string;
  title: string;
  properties: Record<string, never>;
};

type AnalyticsEnvelope = {
  company_id: string;
  asset_id: string;
  visitor_id: string;
  events: PageViewEvent[];
};

const visitorCookieName = 'mutiny_vid';

export function init(options: AnalyticsClientInitOptions): void {
  if (!options.endpoint || !options.companyId || !options.assetId) {
    return;
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  sendPayload(options.endpoint, {
    company_id: options.companyId,
    asset_id: options.assetId,
    visitor_id: getVisitorId(),
    events: [
      {
        event_type: AnalyticsEventType.PageView,
        client_ts: Date.now(),
        url: window.location.href,
        referrer: document.referrer,
        title: document.title,
        properties: {},
      },
    ],
  });
}

function createId(): string {
  return uuidv7();
}

function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmedPart = part.trim();
    if (trimmedPart.startsWith(prefix)) {
      return decodeURIComponent(trimmedPart.slice(prefix.length));
    }
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
}

function getVisitorId(): string {
  const existingVisitorId = readCookie(visitorCookieName);
  if (existingVisitorId) {
    return existingVisitorId;
  }

  const visitorId = createId();
  writeCookie(visitorCookieName, visitorId);
  return visitorId;
}

function sendPayload(endpoint: string, body: AnalyticsEnvelope): void {
  const payload = JSON.stringify(body);
  const blob = new Blob([payload], { type: 'application/json' });

  if (navigator.sendBeacon?.(endpoint, blob)) {
    return;
  }

  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {});
}
